import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAppRouter } from '../../hooks/useAppRouter';
import { HiOutlineDocumentReport as HiOutlineDocumentReportIcon } from 'react-icons/hi';
import { fetchDepartments, fetchSubdepartments } from '../../services/api';
import { useUserGroups } from '../../hooks/useUserGroups';
import CollapsiblePanel from '../shared/CollapsiblePanel';

interface RootState {
  sidebar: {
    departments: string[];
    selectedDept: string | null;
    subdepartments: string[];
    selectedSubdept: string | null;
    loadingDepts: boolean;
    deptsError: string | null;
    loadingSubdepts: boolean;
    subdeptsError: string | null;
    deptSearch: string;
    sidebarCollapsed: boolean;
  };
}

const ReportSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { searchParams, setSearchParams } = useAppRouter();
  
  const departments = useSelector((state: RootState) => state.sidebar.departments);
  const selectedDept = useSelector((state: RootState) => state.sidebar.selectedDept);
  const subdepartments = useSelector((state: RootState) => state.sidebar.subdepartments);
  const selectedSubdept = useSelector((state: RootState) => state.sidebar.selectedSubdept);
  const loadingDepts = useSelector((state: RootState) => state.sidebar.loadingDepts);
  const deptsError = useSelector((state: RootState) => state.sidebar.deptsError);
  const loadingSubdepts = useSelector((state: RootState) => state.sidebar.loadingSubdepts);
  const subdeptsError = useSelector((state: RootState) => state.sidebar.subdeptsError);
  const deptSearch = useSelector((state: RootState) => state.sidebar.deptSearch);
  const sidebarCollapsed = useSelector((state: RootState) => state.sidebar.sidebarCollapsed);
  const { groups: userGroups, loading: groupsLoading } = useUserGroups();
  const hasFetchedDepartments = useRef(false);
  const lastFetchedGroups = useRef<string>('');

  const [isMobile, setIsMobile] = useState(false);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch departments once groups are loaded (or if groups loading is complete)
  useEffect(() => {
    // Wait for groups to finish loading before making the request
    if (groupsLoading) return;
    
    // Create a stable string representation of groups to compare
    const groupsKey = JSON.stringify([...userGroups].sort());
    
    // Only fetch if we haven't fetched yet, or if groups have actually changed
    if (hasFetchedDepartments.current && lastFetchedGroups.current === groupsKey) {
      return;
    }
    
    hasFetchedDepartments.current = true;
    lastFetchedGroups.current = groupsKey;
    
    dispatch({ type: 'sidebar/setLoadingDepts', payload: true });
    dispatch({ type: 'sidebar/setDeptsError', payload: null });
    
    fetchDepartments(userGroups)
      .then(data => {
        dispatch({ type: 'sidebar/setDepartments', payload: Array.isArray(data) ? data : [] });
        dispatch({ type: 'sidebar/setLoadingDepts', payload: false });
      })
      .catch(err => {
        dispatch({ type: 'sidebar/setDeptsError', payload: t('sidebar.couldNotLoadCategories') });
        dispatch({ type: 'sidebar/setLoadingDepts', payload: false });
      });
  }, [dispatch, userGroups, groupsLoading]);

  // Fetch subdepartments when selectedDept changes
  const lastFetchedSubdept = useRef<string>('');
  useEffect(() => {
    // Wait for groups to finish loading before making the request
    if (groupsLoading) return;
    
    if (!selectedDept) {
      dispatch({ type: 'sidebar/setSubdepartments', payload: [] });
      dispatch({ type: 'sidebar/setLoadingSubdepts', payload: false });
      dispatch({ type: 'sidebar/setSubdeptsError', payload: null });
      lastFetchedSubdept.current = '';
      return;
    }
    
    // Create a key that includes both department and groups
    const fetchKey = `${selectedDept}:${JSON.stringify([...userGroups].sort())}`;
    
    // Only fetch if the department or groups have changed
    if (lastFetchedSubdept.current === fetchKey) {
      return;
    }
    
    lastFetchedSubdept.current = fetchKey;
    
    dispatch({ type: 'sidebar/setLoadingSubdepts', payload: true });
    dispatch({ type: 'sidebar/setSubdeptsError', payload: null });
    
    fetchSubdepartments(selectedDept, userGroups)
      .then(data => {
        dispatch({ type: 'sidebar/setSubdepartments', payload: Array.isArray(data) ? data : [] });
        dispatch({ type: 'sidebar/setLoadingSubdepts', payload: false });
      })
      .catch(err => {
        dispatch({ type: 'sidebar/setSubdeptsError', payload: t('sidebar.couldNotLoadSubdepartments') });
        dispatch({ type: 'sidebar/setLoadingSubdepts', payload: false });
      });
  }, [selectedDept, dispatch, userGroups, groupsLoading]);

  // Sync Redux state from URL (URL is the source of truth)
  useEffect(() => {
    const urlDept = searchParams.get('department');
    const urlSubdept = searchParams.get('sub-department');

    // Only update Redux if out of sync
    if (urlDept !== selectedDept) {
      dispatch({ type: 'sidebar/selectDept', payload: urlDept });
      dispatch({ type: 'sidebar/selectSubdept', payload: null });
    }
    if (urlDept === selectedDept && urlSubdept !== selectedSubdept) {
      dispatch({ type: 'sidebar/selectSubdept', payload: urlSubdept });
    }
  }, [searchParams, selectedDept, selectedSubdept, dispatch]);

  const handleDepartmentClick = (dept: string) => {
    // Only update the URL, Redux will sync from URL
    const params = new URLSearchParams(window.location.search);
    
    // If clicking on the already selected department, collapse it (clear selection)
    if (selectedDept === dept) {
      params.delete('department');
      params.delete('sub-department');
    } else {
      // Otherwise, expand it (select the department)
      params.set('department', dept);
      params.delete('sub-department');
    }
    
    setSearchParams(params);
  };

  const handleSubdepartmentClick = (sub: string) => {
    // Only update the URL, Redux will sync from URL
    const params = new URLSearchParams(window.location.search);
    if (selectedDept) params.set('department', selectedDept);
    params.set('sub-department', sub);
    setSearchParams(params);
  };

  const handleToggleCollapse = () => {
    dispatch({ type: 'sidebar/toggleCollapsed' });
    if (isMobile) {
      if (departments.length === 0) {
        dispatch({ type: 'sidebar/setLoadingDepts', payload: true });
        dispatch({ type: 'sidebar/setDeptsError', payload: null });
        fetchDepartments()
          .then(data => {
            dispatch({ type: 'sidebar/setDepartments', payload: Array.isArray(data) ? data : [] });
            dispatch({ type: 'sidebar/setLoadingDepts', payload: false });
          })
          .catch(err => {
            dispatch({ type: 'sidebar/setDeptsError', payload: 'Could not load categories' });
            dispatch({ type: 'sidebar/setLoadingDepts', payload: false });
          });
      }
      setMobileModalOpen(true);
    }
  };

  // Move this inside the component so it always uses the latest Redux state
  const filteredDepartments = departments.filter((dept: string) =>
    dept.toLowerCase().includes(deptSearch.toLowerCase())
  );

  useEffect(() => {
    if (isMobile && mobileModalOpen && departments.length === 0) {
      dispatch({ type: 'sidebar/setLoadingDepts', payload: true });
      dispatch({ type: 'sidebar/setDeptsError', payload: null });
      fetchDepartments()
        .then(data => {
          dispatch({ type: 'sidebar/setDepartments', payload: Array.isArray(data) ? data : [] });
          dispatch({ type: 'sidebar/setLoadingDepts', payload: false });
        })
        .catch(err => {
          dispatch({ type: 'sidebar/setDeptsError', payload: 'Could not load categories' });
          dispatch({ type: 'sidebar/setLoadingDepts', payload: false });
        });
    }
  }, [isMobile, mobileModalOpen, departments.length, dispatch]);

  return (
    <>
      <CollapsiblePanel
        title={t('sidebar.departments')}
        collapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
        panelClassName={`dashboard-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}
        selectedDept={selectedDept}
        selectedSubdept={selectedSubdept}
      >
        {sidebarCollapsed ? (
          <></>
        ) : (
          <ul className="sidebar-list">
            {loadingDepts && (
              <li style={{ padding: '16px', color: '#888', listStyle: 'none' }}>{t('common.loading')}</li>
            )}
            {deptsError && (
              <li style={{ padding: '16px', color: 'red', listStyle: 'none' }}>{deptsError}</li>
            )}
            {!loadingDepts && !deptsError && filteredDepartments.map((dept: string) => (
              <React.Fragment key={dept}>
                <li
                  className={selectedDept === dept ? 'active' : ''}
                  onClick={() => handleDepartmentClick(dept)}
                >
                  {HiOutlineDocumentReportIcon({ className: 'sidebar-icon' })}
                  {dept}
                </li>
                {selectedDept === dept && (
                  <ul className="sidebar-sublist">
                    {loadingSubdepts && (
                      <li className="sidebar-subitem" style={{ color: '#888' }}>{t('common.loading')}</li>
                    )}
                    {subdeptsError && (
                      <li className="sidebar-subitem" style={{ color: 'red' }}>{subdeptsError}</li>
                    )}
                    {!loadingSubdepts && !subdeptsError && subdepartments.map((sub: string) => (
                      <li
                        key={sub}
                        className={`sidebar-subitem${selectedSubdept === sub ? ' active' : ''}`}
                        onClick={() => handleSubdepartmentClick(sub)}
                      >
                        {sub}
                      </li>
                    ))}
                  </ul>
                )}
              </React.Fragment>
            ))}
            {filteredDepartments.length === 0 && !loadingDepts && !deptsError && (
              <li style={{ padding: '8px', color: '#888', fontSize: '0.9rem' }}>{t('sidebar.noDepartments')}</li>
            )}
          </ul>
        )}
      </CollapsiblePanel>
      {isMobile && mobileModalOpen && (
        <div className="sidebar-modal-overlay" onClick={() => setMobileModalOpen(false)}>
          <div className="sidebar-modal" onClick={e => e.stopPropagation()}>
            <button className="sidebar-modal-close" onClick={() => setMobileModalOpen(false)}>&times;</button>
            <div style={{ padding: 16 }}>
              <div style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: 12 }}>{t('sidebar.departments')}</div>
              <input
                className="sidebar-search"
                type="text"
                placeholder={t('common.search')}
                value={deptSearch}
                onChange={e => dispatch({ type: 'sidebar/setDeptSearch', payload: e.target.value })}
                style={{ marginBottom: 12, width: '100%' }}
              />
              <ul className="sidebar-list">
                {loadingDepts && (
                  <li style={{ padding: '16px', color: '#888', listStyle: 'none' }}>Loading...</li>
                )}
                {deptsError && (
                  <li style={{ padding: '16px', color: 'red', listStyle: 'none' }}>{deptsError}</li>
                )}
                {!loadingDepts && !deptsError && filteredDepartments.map((dept: string) => (
                  <React.Fragment key={dept}>
                    <li
                      className={selectedDept === dept ? 'active' : ''}
                      onClick={() => handleDepartmentClick(dept)}
                    >
                      {HiOutlineDocumentReportIcon({ className: 'sidebar-icon' })}
                      {dept}
                    </li>
                    {selectedDept === dept && (
                      <ul className="sidebar-sublist">
                        {loadingSubdepts && (
                          <li className="sidebar-subitem" style={{ color: '#888' }}>Loading...</li>
                        )}
                        {subdeptsError && (
                          <li className="sidebar-subitem" style={{ color: 'red' }}>{subdeptsError}</li>
                        )}
                        {!loadingSubdepts && !subdeptsError && subdepartments.map((sub: string) => (
                          <li
                            key={sub}
                            className={`sidebar-subitem${selectedSubdept === sub ? ' active' : ''}`}
                            onClick={() => { handleSubdepartmentClick(sub); setMobileModalOpen(false); }}
                          >
                            {sub}
                          </li>
                        ))}
                      </ul>
                    )}
                  </React.Fragment>
                ))}
                {filteredDepartments.length === 0 && !loadingDepts && !deptsError && (
                  <li style={{ padding: '8px', color: '#888', fontSize: '0.9rem' }}>No departments</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportSidebar; 